import { api } from "../../../api/apiSlice";

// Branch-wise master (handoff §9.6) - distinct from
// src/features/users/employeesApi.js, which is the Milestone-2 "link to
// employee" search picker used by Create User (different DTO, different
// permission, different tag - "MasterEmployees" here vs "Employees" there).
//
// Salary rows are denormalized, one row per deduction applied (confirmed
// live: an employee with 3 deductions has 3 salary rows sharing the same
// amount/allowance/wb/nhif/nssf/pin, differing only by slno and the single
// `deductions` name each row carries) - same full-list diff pattern as
// Deductions' slabs. An employee with zero deductions still needs at least
// one row to persist amount/allowance at all, so the form always sends one
// row minimum (deductions: null when none picked) rather than replicating
// the legacy app's quirk of silently dropping salary data with no deduction.
export const employeesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getEmployees: builder.query({
      query: (params) => ({ url: "/masters/employees", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((e) => ({ type: "MasterEmployees", id: e.empId })),
              { type: "MasterEmployees", id: "LIST" },
            ]
          : [{ type: "MasterEmployees", id: "LIST" }],
    }),
    getEmployee: builder.query({
      query: (empId) => ({ url: `/masters/employees/${empId}` }),
      providesTags: (result, error, empId) => [{ type: "MasterEmployees", id: empId }],
    }),
    createEmployee: builder.mutation({
      query: (body) => ({ url: "/masters/employees", method: "POST", data: body }),
      invalidatesTags: [{ type: "MasterEmployees", id: "LIST" }],
    }),
    updateEmployee: builder.mutation({
      query: ({ empId, ...body }) => ({
        url: `/masters/employees/${empId}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (result, error, { empId }) => [
        { type: "MasterEmployees", id: empId },
        { type: "MasterEmployees", id: "LIST" },
      ],
    }),
    replaceEmployeeSalaries: builder.mutation({
      query: ({ empId, salaries }) => ({
        url: `/masters/employees/${empId}/salaries`,
        method: "PUT",
        data: { salaries },
      }),
      invalidatesTags: (result, error, { empId }) => [
        { type: "MasterEmployees", id: empId },
      ],
    }),
    // Re-codes the employee (handoff §9.6, Employees.Promote): deactivates
    // the old empId, creates a brand-new one under newEmpCode carrying over
    // every other field, response is the NEW employee's detail - navigate
    // there, not back to the old record. Salary is never carried over
    // (response always has salaries: []) - a separate replaceEmployeeSalaries
    // call against the new empId is needed if the new role's pay differs.
    promoteEmployee: builder.mutation({
      query: ({ empId, ...body }) => ({
        url: `/masters/employees/${empId}/promote`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (result, error, { empId }) => [
        { type: "MasterEmployees", id: empId },
        { type: "MasterEmployees", id: "LIST" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetEmployeesQuery,
  useGetEmployeeQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useReplaceEmployeeSalariesMutation,
  usePromoteEmployeeMutation,
} = employeesApi;
